import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Clock, Mountain, Compass, ChevronRight, Filter, ArrowUpDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CragPage() {
  const { slug } = useParams();
  const { t, getTranslatedContent, language } = useLanguage();
  const [crag, setCrag] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gradeFilter, setGradeFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [sortField, setSortField] = useState('order');
  const [sortDir, setSortDir] = useState('asc');
  const [availableSectors, setAvailableSectors] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cragRes = await axios.get(`${API}/crags/${slug}`);
        setCrag(cragRes.data);
        
        const routesRes = await axios.get(`${API}/routes?crag_id=${cragRes.data.id}`);
        setRoutes(routesRes.data);
        setFilteredRoutes(routesRes.data);
        
        // Extract unique sectors
        const sectors = [...new Set(routesRes.data.map(r => r.sector).filter(s => s && s.trim()))];
        setAvailableSectors(sectors.sort());
      } catch (error) {
        console.error('Error fetching crag:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    let filtered = [...routes];
    
    if (gradeFilter !== 'all') {
      filtered = filtered.filter(r => r.grade.startsWith(gradeFilter));
    }
    
    if (sectorFilter !== 'all') {
      filtered = filtered.filter(r => r.sector === sectorFilter);
    }
    
    filtered.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortDir === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    setFilteredRoutes(filtered);
  }, [routes, gradeFilter, sectorFilter, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const grades = ['4', '5', '6', '7', '8'];

  // Set page title using document.title for better compatibility
  useEffect(() => {
    if (crag && crag.name) {
      document.title = crag.name + ' | Caltavuturo Climbing';
    }
    return () => {
      document.title = 'Caltavuturo Climbing';
    };
  }, [crag]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!crag) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Falesia non trovata</p>
      </div>
    );
  }

  return (
    <div data-testid="crag-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-end">
        <div className="absolute inset-0 z-0">
          <img 
            src={crag.images?.[0] || 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=1200'}
            alt={crag.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        </div>
        
        <div className="container-custom relative z-10 pb-12">
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="text-white/70 hover:text-white">{t('home')}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/crags/gazzara" className="text-white/70 hover:text-white">{t('crags')}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">{crag.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="heading-xl text-white"
          >
            {crag.name}
          </motion.h1>
        </div>
      </section>

      {/* Info Grid */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="heading-md text-[#0A0A0A] mb-6">Descrizione</h2>
              <p className="body-lg text-gray-600 mb-8">
                {getTranslatedContent(crag.description)}
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <Mountain className="w-6 h-6 text-[#FF6D00] mb-2" />
                  <p className="font-heading text-2xl font-bold">{crag.total_routes}</p>
                  <p className="text-sm text-gray-500">{t('totalRoutes')}</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <Filter className="w-6 h-6 text-[#FF6D00] mb-2" />
                  <p className="font-heading text-2xl font-bold">{crag.grade_range}</p>
                  <p className="text-sm text-gray-500">{t('gradeRange')}</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <Clock className="w-6 h-6 text-[#FF6D00] mb-2" />
                  <p className="font-heading text-lg font-bold">{getTranslatedContent(crag.approach_time)}</p>
                  <p className="text-sm text-gray-500">{t('approachTime')}</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <Compass className="w-6 h-6 text-[#FF6D00] mb-2" />
                  <p className="font-heading text-lg font-bold">{getTranslatedContent(crag.exposure)}</p>
                  <p className="text-sm text-gray-500">{t('exposure')}</p>
                </div>
              </div>

              {/* Routes Table */}
              <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                  <h2 className="heading-md text-[#0A0A0A]">{t('routes')} ({filteredRoutes.length})</h2>
                  <div className="flex flex-wrap gap-3">
                    {availableSectors.length > 0 && (
                      <Select value={sectorFilter} onValueChange={setSectorFilter}>
                        <SelectTrigger className="w-40" data-testid="sector-filter">
                          <SelectValue placeholder="Settore" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti i settori</SelectItem>
                          {availableSectors.map(s => (
                            <SelectItem key={s} value={s}>Settore {s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Select value={gradeFilter} onValueChange={setGradeFilter}>
                      <SelectTrigger className="w-40" data-testid="grade-filter">
                        <SelectValue placeholder={t('filterByGrade')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('allGrades')}</SelectItem>
                        {grades.map(g => (
                          <SelectItem key={g} value={g}>{g}x</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {filteredRoutes.length > 0 ? (
                  <div className="bg-white rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => handleSort('name')}
                          >
                            <div className="flex items-center gap-2">
                              Nome via
                              <ArrowUpDown className="w-4 h-4" />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => handleSort('grade')}
                          >
                            <div className="flex items-center gap-2">
                              {t('grade')}
                              <ArrowUpDown className="w-4 h-4" />
                            </div>
                          </TableHead>
                          <TableHead className="hidden md:table-cell">{t('length')}</TableHead>
                          <TableHead className="hidden md:table-cell">{t('type')}</TableHead>
                          {availableSectors.length > 0 && (
                            <TableHead className="hidden md:table-cell">Settore</TableHead>
                          )}
                          <TableHead className="hidden lg:table-cell">Chiodatura</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRoutes.map((route) => (
                          <TableRow key={route.id}>
                            <TableCell className="font-medium">{route.name}</TableCell>
                            <TableCell>
                              <span className="inline-block bg-[#FF6D00]/10 text-[#FF6D00] px-3 py-1 rounded text-sm font-semibold">
                                {route.grade}
                              </span>
                            </TableCell>
                            <TableCell className="hidden md:table-cell">{route.length || '-'}</TableCell>
                            <TableCell className="hidden md:table-cell capitalize">{route.route_type}</TableCell>
                            {availableSectors.length > 0 && (
                              <TableCell className="hidden md:table-cell">
                                {route.sector && (
                                  <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                                    {route.sector}
                                  </span>
                                )}
                              </TableCell>
                            )}
                            <TableCell className="hidden lg:table-cell">{route.bolting || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">{t('noResults')}</p>
                  </div>
                )}
              </div>

              {/* Gallery */}
              {crag.images?.length > 1 && (
                <div>
                  <h2 className="heading-md text-[#0A0A0A] mb-6">{t('gallery')}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {crag.images.map((img, i) => (
                      <div key={i} className="aspect-square overflow-hidden rounded-lg">
                        <img 
                          src={img}
                          alt={`${crag.name} ${i + 1}`}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Info Card */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-heading text-lg uppercase mb-6">Informazioni</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Compass className="w-5 h-5 text-[#FF6D00] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">{t('exposure')}</p>
                      <p className="font-medium">{getTranslatedContent(crag.exposure)}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Mountain className="w-5 h-5 text-[#FF6D00] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">{t('rockType')}</p>
                      <p className="font-medium">{getTranslatedContent(crag.rock_type)}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#FF6D00] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">{t('approachTime')}</p>
                      <p className="font-medium">{getTranslatedContent(crag.approach_time)}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#FF6D00] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">{t('pathType')}</p>
                      <p className="font-medium">{getTranslatedContent(crag.path_type)}</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* GPS */}
              {crag.gps_lat && crag.gps_lng && (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="font-heading text-lg uppercase mb-4">{t('gpsCoordinates')}</h3>
                  <p className="font-mono text-sm mb-4">
                    {crag.gps_lat.toFixed(6)}, {crag.gps_lng.toFixed(6)}
                  </p>
                  <a 
                    href={`https://www.google.com/maps?q=${crag.gps_lat},${crag.gps_lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#FF6D00] font-semibold hover:underline"
                  >
                    Apri in Google Maps
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </a>
                </div>
              )}

              {/* Map Embed */}
              <div className="rounded-lg overflow-hidden h-64">
                <iframe
                  title="Crag Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8'}&q=${crag.gps_lat},${crag.gps_lng}&zoom=14`}
                />
              </div>

              {/* Related Crags */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-heading text-lg uppercase mb-4">Altre falesie</h3>
                <ul className="space-y-2">
                  {['gazzara', 'cabeci', 'rocca-piccola', 'rocca-di-sciara'].filter(s => s !== slug).slice(0, 4).map(s => (
                    <li key={s}>
                      <Link 
                        to={`/crags/${s}`}
                        className="flex items-center justify-between py-2 text-gray-600 hover:text-[#FF6D00] transition-colors"
                      >
                        <span className="capitalize">{s.replace(/-/g, ' ')}</span>
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
